import React, { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: IconDefinition;
  rightIcon?: IconDefinition;
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  loading = false,
  fullWidth = false,
  disabled,
  children,
  className = '',
  ...props
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  const variantClasses = {
    primary: 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-400',
    secondary: 'text-gray-700 bg-gray-100 hover:bg-gray-200 focus:ring-gray-500 border border-gray-300',
    danger: 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500 disabled:bg-red-400',
    success: 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500 disabled:bg-green-400',
    outline: 'text-blue-600 bg-transparent hover:bg-blue-50 focus:ring-blue-500 border border-blue-600 hover:border-blue-700'
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${className}`.trim();
  
  const isDisabled = disabled || loading;
  
  const renderIcon = (icon: IconDefinition, position: 'left' | 'right') => {
    const iconSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
    const marginClass = position === 'left' ? 'mr-2' : 'ml-2';
    
    return (
      <FontAwesomeIcon 
        icon={icon} 
        className={`${iconSize} ${marginClass}`}
      />
    );
  };
  
  const renderLoadingSpinner = () => {
    const spinnerSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
    
    return (
      <div 
        className={`animate-spin rounded-full border-2 border-transparent border-t-current mr-2 ${spinnerSize}`}
      />
    );
  };

  return (
    <button
      ref={ref}
      className={buttonClasses}
      disabled={isDisabled}
      {...props}
    >
      {loading && renderLoadingSpinner()}
      {!loading && leftIcon && renderIcon(leftIcon, 'left')}
      {children}
      {!loading && rightIcon && renderIcon(rightIcon, 'right')}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;